"use client";
import Link from "next/link";
import { ArrowLeft, Clock, TrendingUp, Camera } from "lucide-react";
import { useMemo } from "react";
import { cleanHeaderTags, createLookupObject, decodeHtml } from "@/lib/helper";
import { ICMS } from "@/store/home/homeModel";
import { OptimizedImage } from "@/components/common/OptimizedImage";

interface ActivityDetailClientProps {
  activityUrl: string;
  activityData: any;
  cmsData: ICMS[];
}

export default function ActivityDetailClient({
  activityUrl,
  activityData,
  cmsData,
}: ActivityDetailClientProps) {
  const lookUpCMSData = useMemo(() => {
    return createLookupObject(cmsData || [], "Name");
  }, [cmsData]);

  const ActivityDetail = activityData;

  return (
    <>
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-end mt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80 z-10" />
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src={ActivityDetail?.BannerImage}
            alt={ActivityDetail?.ActivityName || "Activity Detail"}
            fill
            priority
            className="object-cover"
          />
        </div>
        <div className="relative z-20 w-full max-w-6xl mx-auto px-6 pb-16">
          <Link
            href="/activities/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Activities
          </Link>
          <h1 className="text-white drop-shadow-2xl">
            {ActivityDetail?.ActivityName}
          </h1>
        </div>
      </section>

      {/* Quick Info Bar */}
      <section className="bg-[#1F3D2B] py-6">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-3 gap-8 text-center text-white">
            <div>
              <Clock className="w-6 h-6 mx-auto mb-2 text-[#E07A2F]" />
              <div className="text-lg font-medium">
                {ActivityDetail?.Duration}
              </div>
              <div className="text-sm text-white/85">Duration</div>
            </div>
            <div>
              <TrendingUp className="w-6 h-6 mx-auto mb-2 text-[#E07A2F]" />
              <div className="text-lg font-medium">
                {ActivityDetail?.DifficultyType}
              </div>
              <div className="text-sm text-white/85">Difficulty</div>
            </div>
            <div>
              <Camera className="w-6 h-6 mx-auto mb-2 text-[#E07A2F]" />
              <div className="text-lg font-medium">
                {ActivityDetail?.Availability}
              </div>
              <div className="text-sm text-white/85">Availability</div>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 bg-gradient-to-b from-white to-[#F3EBDD]/30">
        <div className="max-w-4xl mx-auto px-6">
          <div
            className="cms-section"
            dangerouslySetInnerHTML={{
              __html: cleanHeaderTags(decodeHtml(ActivityDetail?.Description)),
            }}
          />
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-[#5A3A1E] mb-12">What to Expect</h2>
          <div className="space-y-8">
            {ActivityDetail?.ActivityTimeline &&
              ActivityDetail?.ActivityTimeline?.length > 0 &&
              ActivityDetail?.ActivityTimeline.map((step: any, i: number) => (
                <div key={i} className="flex gap-6">
                  <div className="flex-shrink-0 w-24 text-[#BF5500] font-medium">
                    {step.Time}
                  </div>
                  <div className="flex-1 pb-8 border-l-2 border-[#D8C4A8] pl-6 relative">
                    <div className="absolute left-[-9px] top-0 w-4 h-4 bg-[#BF5500] rounded-full border-4 border-white" />
                    <h3
                      className="text-xl mb-2 text-[#5A3A1E]"
                      style={{ fontFamily: "Playfair Display, serif" }}
                    >
                      {step.Title}
                    </h3>
                    <p className="text-[#5A3A1E]/85">{step.Description}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Tips & Inclusions */}
      <section className="py-16 bg-gradient-to-b from-white to-[#F3EBDD]/30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3
                className="text-2xl mb-6 text-[#5A3A1E]"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                What's Included
              </h3>
              <div
                className="cms-section what-Included"
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(ActivityDetail?.WhatsInclude),
                }}
              />
            </div>
            <div>
              <h3
                className="text-2xl mb-6 text-[#5A3A1E]"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Important Tips
              </h3>
              <div
                className="cms-section"
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(
                    decodeHtml(ActivityDetail?.ImportantTips),
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {lookUpCMSData?.["activity - CTA detail page"] && (
        <div
          dangerouslySetInnerHTML={{
            __html: cleanHeaderTags(
              decodeHtml(
                lookUpCMSData?.["activity - CTA detail page"]?.Template,
              ),
            ),
          }}
        />
      )}
    </>
  );
}
